import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { RepeatOffer } from "@/components/RepeatOffer";

export const Stars = ({ value = 0, onChange, size = "h-5 w-5", testId }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid={testId} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} data-testid={testId ? `${testId}-${n}` : undefined} aria-label={`${n} star${n > 1 ? "s" : ""}`}
          onMouseEnter={() => onChange && setHover(n)} onClick={() => onChange?.(n)} className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star className={`${size} transition-colors ${(hover || value) >= n ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />
        </button>
      ))}
    </div>
  );
};

const LABELS = ["", "Poor", "Fair", "Good", "Very good", "Excellent"];

export const PortalFeedback = ({ studyId }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    api.get(`/portal/studies/${studyId}/feedback`).then((r) => { if (r.data) { setSaved(r.data); setRating(r.data.rating); setComment(r.data.comment || ""); } }).catch(() => {})
      .finally(() => { if (window.location.hash === "#feedback") setTimeout(() => document.getElementById("feedback")?.scrollIntoView({ behavior: "smooth", block: "center" }), 600); });
  }, [studyId]);
  const submit = async () => {
    if (!rating) return toast.error("Please choose a star rating");
    setBusy(true);
    try { const r = await api.put(`/portal/studies/${studyId}/feedback`, { rating, comment }); setSaved(r.data); setOffer(r.data.offer); toast.success("Thank you for your feedback!"); }
    catch (e) { toast.error(errMsg(e, "Could not save feedback")); } finally { setBusy(false); }
  };
  return (
    <section id="feedback" className="no-print rounded-xl border border-slate-800 bg-slate-900/70 p-6 scroll-mt-24" data-testid="portal-feedback">
      <h2 className="font-display text-lg font-semibold">{saved ? "Your rating" : "How did we do?"}</h2>
      <p className="text-sm text-slate-400 mb-4">{saved ? "You can update your rating at any time." : "Rate this study. Your feedback goes straight to the team."}</p>
      <div className="flex items-center gap-3 mb-4"><Stars value={rating} onChange={setRating} size="h-7 w-7" testId="feedback-stars" /><span className="text-sm text-amber-300" data-testid="feedback-label">{LABELS[rating]}</span></div>
      <Textarea data-testid="feedback-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional: what was most useful, or what could be better?" className="bg-slate-950/60 border-slate-700 mb-3" />
      <Button data-testid="feedback-submit" disabled={busy} onClick={submit} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{saved ? "Update rating" : "Submit rating"}</Button>
      {offer && <div className="mt-4"><RepeatOffer offer={offer} dark /></div>}
    </section>
  );
};

export const StudyFeedback = ({ studyId }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/studies/${studyId}/feedback`).then((r) => setItems(r.data)).catch(() => {}); }, [studyId]);
  if (!items.length) return null;
  return (
    <section className="no-print rounded-xl border border-slate-800 bg-slate-900/70 p-5 space-y-3" data-testid="study-feedback">
      <h2 className="font-display font-semibold">Client rating</h2>
      {items.map((f) => (
        <div key={f.id || f.client_id} className="flex flex-wrap items-start gap-3">
          <Stars value={f.rating} size="h-4 w-4" testId={`study-feedback-stars-${f.client_id}`} />
          <div className="text-sm"><span className="text-slate-300">{f.client_name}</span><span className="text-xs text-slate-500"> · {f.updated_at?.slice(0, 10)}</span>
            {f.comment && <p className="text-slate-400 mt-0.5">“{f.comment}”</p>}</div>
        </div>
      ))}
    </section>
  );
};

export const SatisfactionPanel = ({ data }) => {
  if (!data?.count) return <p className="max-w-7xl mx-auto text-sm text-slate-500 mb-6" data-testid="satisfaction-empty">No client ratings yet. Clients can rate delivered studies in their portal.</p>;
  return (
    <section className="max-w-7xl mx-auto rounded-xl border border-slate-800 bg-slate-900/70 p-6 mb-8" data-testid="satisfaction-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <h2 className="font-display text-lg font-semibold">Client satisfaction</h2>
        <p className="flex items-center gap-2 text-sm"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /><span className="num font-bold text-amber-300" data-testid="satisfaction-overall">{data.overall.toFixed(1)}</span><span className="text-slate-500">overall · {data.count} rating{data.count > 1 ? "s" : ""}</span></p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.staff.map((s) => (
          <div key={s.staff_id} className="min-w-0 rounded-lg border border-slate-800 bg-slate-950/50 p-4" data-testid={`satisfaction-staff-${s.staff_id}`}>
            <div className="flex items-center justify-between mb-2"><p className="font-semibold">{s.name}</p><p className="num text-amber-300 font-bold">{s.average.toFixed(1)} <span className="text-xs text-slate-500 font-normal">({s.count})</span></p></div>
            <div className="space-y-1 mb-3">
              {[5, 4, 3, 2, 1].map((n) => (
                <div key={n} className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="w-3 num">{n}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${(s.dist[n - 1] / s.count) * 100}%` }} /></div>
                  <span className="w-4 num text-right">{s.dist[n - 1]}</span>
                </div>
              ))}
            </div>
            {s.recent.filter((r) => r.comment).slice(0, 2).map((r, i) => <p key={i} className="text-xs text-slate-400 italic truncate">“{r.comment}” — {r.client_name}</p>)}
          </div>
        ))}
      </div>
    </section>
  );
};

export const RatingRequestButton = ({ study }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (study.client_id && study.status === "ready") api.get(`/studies/${study.id}/rating-request`).then((r) => setSt(r.data)).catch(() => {}); }, [study.id, study.client_id, study.status]);
  if (!study.client_id || study.status !== "ready" || !st || st.rated) return null;
  const send = async () => {
    setBusy(true);
    try { const r = await api.post(`/studies/${study.id}/rating-request`); setSt(r.data); toast.success(`Rating request sent to ${r.data.sent_to}`); }
    catch (e) { toast.error(errMsg(e, "Could not send rating request")); } finally { setBusy(false); }
  };
  const n = st.rating_request_count;
  return (
    <div className="no-print flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/70 px-5 py-3" data-testid="rating-request">
      <Star className="h-4 w-4 text-amber-400" />
      <span className="text-sm text-slate-300 flex-1" data-testid="rating-request-status">
        {!st.eligible ? `Rating request: ${st.reason}` : n ? `Rating requested ${n} time${n > 1 ? "s" : ""} · last ${st.rating_requested_at?.slice(0, 10)}` : "No rating request sent yet"}
      </span>
      <Button data-testid="rating-request-btn" size="sm" variant="outline" disabled={busy || !st.eligible} onClick={send} className="rounded-full border-slate-700 bg-transparent">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send rating request now
      </Button>
    </div>
  );
};
